import type { FC } from "react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import cn from "classnames";
import { api, useAppSelector, useAppDispatch } from "@litres-core/store";
import { Dropdown } from "@litres/ui-kit/dropdown";

import { GenresTreePopup } from "@litres-modules/genres-tree-popup";
import {
  getCookie,
  deleteCookie,
  focusNextInElement,
  findFocussableNextSiblingElem,
} from "@litres/utils";
import {
  useEventListener,
  useHasWindow,
  EPlatform,
} from "@litres-libs/global-hooks";
import { SmartLink } from "@litres-modules/smart-link";
import { initializeTranslations, useTranslation } from "@litres-libs/i18n";
import { SkeletonBlock } from "@litres/ui-kit/skeleton";
import { LoaderTransition } from "@litres/ui-kit/loader-transition";
import { EPopups } from "../../utils/enums";
import { useScrollPosition } from "../../hooks/useScrollPosition";
import { NewSearch } from "../NewSearch/NewSearch";
import { SuggestionsSearch } from "../SuggestionsSearch";
import { Logo } from "../Logo/Logo";
import { NavBar } from "./NavBar";
import { LowerMenuV2 } from "../LowerMenuV2";
import { Banners } from "../Banners/Banners";
import type { IPrevAndCurrScrollPos } from "../../hooks/useScrollPosition";
import { LibraryLogo } from "../LibraryLogo/LibraryLogo";
import { HeaderContext } from "../../Header";
import { hideNewHeaderForLibrarian } from "../../utils/helpers";
import { useShouldShowToggleBannner } from "../../hooks/useShouldShowToggleBannner";
import { CatalogButton } from "./CatalogButton";
import { useRouterLoading } from "../../hooks/useRouterLoading";
import { DROPDOWN_APP_LANGUAGES } from "../../constants";
import { goToUrl } from "../../utils/goToUrl";
import { DropdownLanguageItem } from "../DropdownLanguageItem";
import styles from "./HeaderDesktop.module.scss";

const ToggleBanner = lazy(() => import("../ToggleBanner/ToggleBanner"));

initializeTranslations();

interface IHeaderProps {
  pathname?: string;
}

const DEFAULT_LOWER_MENU_HEIGHT = 40;
// Высота блока баннера сверху
const TOP_BANNER_HEIGHT = 50;

export const HeaderDesktop: FC<IHeaderProps> = ({ pathname }) => {
  const { t } = useTranslation();
  const isRouterLoading = useRouterLoading();
  const dispatch = useAppDispatch();
  const {
    isLibraryDomain,
    isSchoolDomain,
    isSubscriptionDomain,
    isMobileOperatorDomain,
    isMainDomain,
    isPdaDomain,
    isForeignDomain,
  } = useContext(HeaderContext);

  const hasWindow = useHasWindow();

  const appLanguages = DROPDOWN_APP_LANGUAGES.map(item => ({
    id: item.id,
    value: item.value,
  }));

  const [lowerMenuHeight, setLowerMenuHeight] = useState(
    DEFAULT_LOWER_MENU_HEIGHT
  );
  const [isNarrowHeader, setNarrow] = useState<boolean>(false);
  const [openedPopup, setOpenedPopup] = useState<EPopups>(EPopups.Default);
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(true);
  const isSubBannerVisible = useShouldShowToggleBannner({
    isSchoolDomain,
    isLibraryDomain,
    isForeignDomain,
    isSubscriptionDomain,
    isMobileOperatorDomain,
  });
  const isRouteTranstion = useRouterLoading();

  const isGenresVisibility = useAppSelector(
    state => state.global.genres.popup.isOpen
  );
  const headerHeight = useAppSelector(
    state => state.global.interface.headerHeight
  );
  const headerFullHeight =
    headerHeight + (isNarrowHeader ? lowerMenuHeight : 0);

  const lang = useAppSelector(state => state.face.language) || "ru";
  const dropdownLang = appLanguages.find(item => item.id === lang);

  const {
    id: userId,
    profile: userProfile,
    library_info,
  } = useAppSelector(state => state.foundationUser);

  const isShowBanner = useAppSelector<boolean>(
    state =>
      state.global.header.banners.onlyLitres.isActive ||
      state.global.header.banners.promotion.isActive ||
      state.global.header.banners.subscr.isActive ||
      state.global.header.banners.tvCampaignBanner.isActive ||
      false
  );

  hideNewHeaderForLibrarian(
    library_info.is_librarian || false,
    userId === 0,
    lang
  );

  const headerRef = useCallback(
    (element: HTMLElement) => {
      if (hasWindow && element !== null) {
        const headerHeight = element.getBoundingClientRect().height;
        dispatch(api.global.interfaceRequests.setHeaderHeight(headerHeight));

        // получаем высоту меню именно из lowerMenuWrap
        const currentLowerMenuHeight = document
          .getElementById("lowerMenuWrap")
          ?.getBoundingClientRect().height;

        // сохраняем полученную высоту меню (нужно для подложки меню)
        if (
          currentLowerMenuHeight &&
          currentLowerMenuHeight !== lowerMenuHeight
        ) {
          setLowerMenuHeight(currentLowerMenuHeight);
        }
      }
    },
    [
      hasWindow,
      isNarrowHeader,
      isShowBanner,
      isSubBannerVisible,
      isBannerVisible,
      isRouteTranstion,
    ]
  );

  useEffect(() => {
    if (isGenresVisibility && openedPopup !== EPopups.Genres) {
      dispatch(api.global.genres.setOpenPopup(false));
    }
  }, [openedPopup, isGenresVisibility]);

  const hideGenres = useCallback(() => {
    dispatch(api.global.genres.setOpenPopup(false));
  }, [isGenresVisibility]);
  const toggleGenres = useCallback(() => {
    dispatch(api.global.genres.setOpenPopup(!isGenresVisibility));
  }, [isGenresVisibility]);

  const genresRef = useRef<HTMLDivElement>(null);
  const genresButtonRef = useRef<HTMLDivElement>(null);
  const genresNewButtonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: Event): void => {
    if (
      genresRef.current &&
      (event.target === genresRef.current ||
        genresRef.current.contains(event.target as Node))
    ) {
      return;
    }

    if (
      genresButtonRef.current &&
      (event.target === genresButtonRef.current ||
        genresButtonRef.current.contains(event.target as Node))
    ) {
      return;
    }
    if (
      genresNewButtonRef.current &&
      (event.target === genresNewButtonRef.current ||
        genresNewButtonRef.current.contains(event.target as Node))
    ) {
      return;
    }

    hideGenres();
  };

  const skipToMain = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    let el: Element = document.getElementsByTagName("main")?.[0];
    const breadcrumbs = document.getElementById("breadcrumbs");
    if (breadcrumbs) {
      el = findFocussableNextSiblingElem(breadcrumbs.parentElement) || el;
    }
    focusNextInElement(el);
  };

  useEventListener("click", handleClick);

  const handleScroll = useCallback(
    (params: IPrevAndCurrScrollPos): void => {
      const { prevPos, currPos } = params;
      const isDownScroll = currPos.y < prevPos.y;
      const isUpScroll = currPos.y > prevPos.y;

      if (currPos.y > -TOP_BANNER_HEIGHT) {
        setIsBannerVisible(true);
      } else {
        // Скрываем баннер, если скроллим больше высоты баннера
        setIsBannerVisible(false);
      }

      const genresPopupCoords = genresRef.current?.getBoundingClientRect();

      if (
        isGenresVisibility &&
        genresPopupCoords &&
        genresPopupCoords.bottom < headerHeight
      ) {
        dispatch(api.global.genres.setOpenPopup(false));
      }

      const defaultPositionTop = headerHeight;

      if (isDownScroll) {
        if (Math.abs(currPos.y) > 99) {
          setNarrow(true);
        }

        const startPositionTop = genresPopupCoords
          ? genresPopupCoords.top
          : defaultPositionTop;
        if (genresRef.current) {
          const delta = Math.abs(currPos.y - prevPos.y);
          // Если попап был открыт смещаем на дельту сдвига, иначе ставим стартовую позицию
          genresRef.current.style.top = `${
            isGenresVisibility ? startPositionTop - delta : defaultPositionTop
          }px`;
        }
      }

      if (isUpScroll) {
        setNarrow(false);

        if (genresRef.current && genresPopupCoords) {
          genresRef.current.style.top = `${defaultPositionTop}px`;
        }
      }
    },
    [headerHeight, isGenresVisibility, dispatch]
  );

  useScrollPosition(handleScroll, null, false, 100);

  useEffect(() => {
    const checkOldUserReminder = getCookie("checkOldUserReminder");
    if (checkOldUserReminder && userProfile.registered_at) {
      deleteCookie("checkOldUserReminder");
    }
  }, []);

  return (
    <>
      {headerHeight > 0 && (
        <div
          className={styles.headerGap}
          style={{
            paddingBottom: headerFullHeight,
          }}
        />
      )}
      <header
        className={cn(styles.wrapper, {
          [styles.narrowWrapper]: isNarrowHeader,
          [styles.wrapper_fixed]: headerHeight > 0,
        })}
        data-testid="header"
        ref={headerRef}
        role="banner"
      >
        <SmartLink
          className={styles.hidden}
          tabIndex={0}
          onClick={skipToMain}
          role="button"
        >
          {t("Перейти к контенту")}
        </SmartLink>
        <Banners isVisible={isBannerVisible} />
        <div className={styles.mainSection}>
          <Logo pathname={pathname} />
          {(library_info.is_library_user || isLibraryDomain) &&
            library_info.logo?.url && (
              <LibraryLogo
                logoPath={library_info.logo.url}
                logoRatio={library_info.logo.ratio}
              />
            )}
          {dropdownLang && !isMainDomain && (
            <div className={styles.langBlock}>
              <Dropdown
                defaultActiveId={dropdownLang?.id}
                items={appLanguages}
                onChange={goToUrl}
                className={styles.langDropdown}
                menuClassName={styles.langItem}
                renderContentItem={id => (
                  <DropdownLanguageItem
                    id={id}
                    defaultActiveId={dropdownLang?.id}
                  />
                )}
              />
            </div>
          )}
          <div className={styles.catalogButton}>
            <CatalogButton
              setOpenedPopup={setOpenedPopup}
              toggleGenres={toggleGenres}
              genresButtonRef={genresNewButtonRef}
            />
            <GenresTreePopup
              id="genres_popup"
              data-testid="genres_popup"
              externalRef={genresRef}
              genresButtonRef={genresNewButtonRef}
              className={styles.genresWrapper}
              style={{ top: headerHeight }}
              hasOverlay
            />
          </div>
          <div className={cn(styles.newSearch)}>
            {/*
              поиск по подсказкам (desktop) или спотлайт
              https://docs.litres.ru/pages/viewpage.action?pageId=98630557
            */}
            {isLibraryDomain ||
            isMobileOperatorDomain ||
            isSubscriptionDomain ||
            isMainDomain ||
            isPdaDomain ? (
              <SuggestionsSearch platform={EPlatform.desktop} />
            ) : (
              <NewSearch />
            )}
          </div>
          <NavBar setOpenedPopup={setOpenedPopup} openedPopup={openedPopup} />
        </div>
        <div
          id="lowerMenuWrap"
          className={cn(styles.lowerMenuWrap, {
            [styles.lowerMenuWrap__none]: isNarrowHeader,
          })}
        >
          <LowerMenuV2
            setOpenedPopup={setOpenedPopup}
            openedPopup={openedPopup}
            isPDA={false}
            isNarrowHeader={isNarrowHeader}
          />
          {isSubBannerVisible && (
            <Suspense fallback={<SkeletonBlock width="100%" height={50} />}>
              <ToggleBanner />
            </Suspense>
          )}
        </div>
        {isRouterLoading && <LoaderTransition />}
      </header>
    </>
  );
};
